import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { ABPageSelect, ABPromoSelect } from '../lib/ab/select-admin';
import { ABObjectCount } from '../lib/ab/widgets-admin';
import { f_pages } from '../lib/mdl/f-page';
import { f_routes, FApp, FRouteTargetType, FRouteMatchFlag, FBuiltinPage, FRoute } from '../lib/mdl/f-route';
import { p_promos } from '../lib/mdl/p-promo-admin';
import { canManageLockedRoutes } from '../lib/me/can';
import { UIInputAdapter } from '../lib/ui/component/adapter';
import { UIButton, makeActionButton } from '../lib/ui/component/button';
import { UII18NInput } from '../lib/ui/component/i18n-input';
import { UIImageInput } from '../lib/ui/component/image';
import { UITextInput, UINumberInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UIList } from '../lib/ui/component/list';
import { UISelect, UIFlagsSelect } from '../lib/ui/component/select';
import { UITableDefinition, UITable } from '../lib/ui/component/table';
import { uiToast } from '../lib/ui/component/toast';
import { UISwitch, UICheckbox } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { UIValue, UIRelPos } from '../lib/ui/types';
import { UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource } from '../lib/y/data-source';
import { $log, xavis_experiments } from '../lib/y/debug';
import { $on } from '../lib/y/events';
import { $form } from '../lib/y/form';
import { yIsEmptyI18N } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { YId } from '../lib/y/types';

interface RouteScreenOptions {
    page_id?: YId;
}

export class AdminRouteScreen extends UIScreen {
    constructor(private _route_id?: YId, private _options: RouteScreenOptions = {}) {
        super();
        this.title = _route_id ? 'Route' : 'New route';
    }

    protected override async fillContent(content: UIViewSpec) {
        content.classList.add('tl-admin-page', 'm-sidebar');

        const options = this._options;
        const route = this._route_id && await f_routes.get(this._route_id);

        if(route)
            this.title = route.path;

        const can_locked = canManageLockedRoutes();
        const can_edit = canManageLockedRoutes() || !route || !route.is_locked;

        const sidebar = $el.DIV('tl-sidebar');

        const app_values: UIValue<FApp>[] = [
            { key: 'home', name: 'Home page' },
            { key: 'producer', name: 'Producer admin' }
        ]

        const target_type_values: UIValue<FRouteTargetType>[] = [
            { key: 'page', name: 'Custom page' },
            { key: 'builtin', name: 'Builtin page' },
            { key: 'server_redirect', name: 'Server redirect' },
            { key: 'client_redirect', name: 'Browser redirect' },
        ];

        const flag_values: UIValue<FRouteMatchFlag>[] = [
            { key: 'logged_in', name: 'Logged in' },
            { key: 'logged_out', name: 'Logged out' },
            { key: 'subscriptions', name: 'Has subscriptions' },
            { key: 'no_subscriptions', name: 'No subscription (status none)' }
        ];

        const builtin_pages: FBuiltinPage[] = ['booking', 'bookings', 'event', 'reviews', 'giftcard', 'invite', 'membership', 'player', 'past', 'producer', 'profile', 'redeem_code', 'search', 'signup', 'subscribe', 'tag', 'venue', 'giftcard_b2b', 'preview_page'];

        if(xavis_experiments) {
            flag_values.push({ key: 'xavi', name: "Xavi's experiments" });
            builtin_pages.push('friends');
        }

        builtin_pages.sort();

        const app = new UISelect(app_values, { required: true, disabled: !can_edit });
        const is_locked = new UISwitch({ disabled: !can_locked });
        const is_hidden = new UISwitch;
        const path = new UITextInput({ required: true, disabled: !can_edit });
        const target_type = new UISelect(target_type_values, { required: true, disabled: !can_edit });
        const match_flags = new UIFlagsSelect(flag_values, { disabled: !can_edit });
        const target_builtin = new UISelect(builtin_pages.map(k => ({ key: k, name: k })), { disabled: !can_edit });
        const target_url = new UITextInput({ disabled: !can_edit });
        const target_page_id = new ABPageSelect({ disabled: !can_edit });
        const promo_id = new ABPromoSelect({ null_promo: true, disabled: !can_edit });
        const title = new UII18NInput(new UITextInput);
        const description = new UII18NInput(new UITextInput({ lines: 4 }));
        const og_title = new UII18NInput(new UITextInput);
        const og_description = new UII18NInput(new UITextInput({ lines: 4 }));
        const og_image = new UII18NInput(new UIImageInput({ scope: 'route', aspect: 1200 / 630 }));
        const in_sitemap = new UICheckbox({ title: 'Show in sitemap' });
        const sitemap_priority = new UINumberInput;
        const no_index = new UICheckbox({ title: 'noindex' });
        const no_follow = new UICheckbox({ title: 'nofollow' });

        const parameters_adapter = new UIInputAdapter((_s: { key: string, value: string }, entry) => {
            const key = new UITextInput({ placeholder: 'Key', disabled: !can_edit });
            const value = new UITextInput({ placeholder: 'Value', disabled: !can_edit });
            const remove = new UIButton({ icon: UIIcon.TRASH_CAN, on_click: () => { parameters_adapter.remove(entry) } });

            return {
                view: $el.DIV('l-input-row', [key, value, can_edit && remove]),
                input: $form({ key: key, value: value })
            };
        })

        const parameters_list = new UIList(parameters_adapter, { classes: 'l-form' });
        const parameters = $form.adapt(parameters_adapter, v => Object.fromEntries(v.map(e => [e.key, e.value])), v => Object.entries(v).map(e => ({ key: e[0], value: e[1] })))

        const form = $form({ is_locked, is_hidden, app, path, match_flags, target_type, target_builtin, target_url, target_page_id, promo_id, parameters, page_metadata: $form({ title, description, og_title, og_description, og_image, in_sitemap, sitemap_priority, no_index, no_follow }) });

        $on(target_type, 'value', v => {
            target_url.ui_show(undefined, v == 'client_redirect' || v == 'server_redirect');
            target_builtin.ui_show(undefined, v == 'builtin');
            target_page_id.ui_show(undefined, v == 'page');
            match_flags.ui_show(undefined, v != null && v != 'server_redirect');
            promo_id.ui_show(undefined, v == 'page' || v == 'builtin');
        });

        const section = $el.FORM('ui-section w-form l-form', [
            new UILabeled(is_locked, { before: 'Locked' }),
            can_locked && new UILabeled(is_hidden, { before: 'Hidden' }),
            new UILabeled(app, { above: 'App' }),
            new UILabeled(path, { above: 'Path' }),
            new UILabeled(target_type, { above: 'Target type' }),
            new UILabeled(match_flags, { above: 'Restrict route' }),
            new UILabeled(target_builtin, { above: 'Page' }),
            new UILabeled(target_url, { above: 'URL' }),
            new UILabeled(target_page_id, { above: 'Page' }),
            new UILabeled(promo_id, { above: 'Promo' }),
            can_locked && new UILabeled(parameters_list, { above: 'Parameters' }),
            can_locked && new UIButton({ title: '+ Add parameter', kind: 'k-text', on_click: () => { parameters_adapter.push({ key: '', value: '' }, true) } }),
            $el.H3('heading', 'Page metadata'),
            $el.P('input-hint', 'Will be used instead of the default one if provided'),
            new UILabeled(title, { above: 'Title' }),
            new UILabeled(description, { above: 'Description' }),
            new UILabeled(og_title, { above: 'OG title' }),
            new UILabeled(og_description, { above: 'OG description' }),
            new UILabeled(og_image, { above: 'OG image', below: 'Recommended: 1200x630 ~100kb', classes: 'r-desktop' }),
            in_sitemap,
            new UILabeled(sitemap_priority, { above: 'Sitemap priority', below: '100 is highest priority', classes: 'r-desktop' }),
            no_index,
            no_follow
        ]);

        sitemap_priority.ui_hide();
        $on(in_sitemap, 'value', v => { sitemap_priority.ui_show(undefined, !!v) });

        if(route) {
            form.value = route;
        } else {
            if(options.page_id) {
                target_type.value = 'page';
                target_page_id.value = options.page_id;
            } else {
                target_type.value = undefined;
                sitemap_priority.value = 50;
            }
        }

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                if(!form.validate()) return;

                $log('app', 'Saving route', form.value, promo_id.value);

                f_routes.upsert(route?.id, form.value).then(() => {
                    $location.pop();
                }, uiToast);
            }
        });

        addTooltip(route ? 'Update' : 'Create', button, { offset: 8, hover: button, position: UIRelPos.Above });

        content.ui_add([section, sidebar, button]);
    }
}

export class AdminRoutesScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        //TODOURGENT: use the partial pages list
        await Promise.all([f_pages.getAll(), f_routes.getAll()]);

        const doSearch = () => {
            let routes = f_routes.all;
            if(!xavis_experiments)
                routes = routes.filter(r => !r.match_flags['xavi'] && !r.is_hidden);

            count.number = routes.length;
            source.data = routes;
        }

        const count = new ABObjectCount('routes');

        const filter_bar = new ABFilterBar({
            right_content: [count],
            no_header: true
        });

        const def = new UITableDefinition<FRoute>();

        def.addValue(r => r.is_locked, $fmt.CHECKICON(UIIcon.LOCK_KEYHOLE), { width: -32 });
        def.addValue(r => r.app, { title: 'App', width: -90 });
        def.addValue(r => r.path, { title: 'Path', width: 200 });
        def.addValue(r => Object.keys(r.match_flags).join(', '), { title: 'Restrictions', width: 200 });
        def.addValue(p => {
            if(p.target_type == 'client_redirect' || p.target_type == 'server_redirect')
                return 'Redirect to ' + p.target_url;
            if(p.target_type == 'page')
                return 'Custom page - ' + f_pages.maybe(p.target_page_id!)?.name;
            if(p.target_type == 'builtin')
                return 'Builtin page - ' + p.target_builtin;
            return 'Unknown';
        }, { title: 'Target', width: 300 });
        def.addValue(p => {
            if(p.promo_id) {
                const promo = p_promos.maybe(p.promo_id);
                if(promo)
                    return promo.name;
            }
            return '';
        }, { title: 'Promo', width: -150 });
        if(xavis_experiments)
            def.addValue(p => !yIsEmptyI18N(p.page_metadata.title) || !yIsEmptyI18N(p.page_metadata.description) || !yIsEmptyI18N(p.page_metadata.og_title) || !yIsEmptyI18N(p.page_metadata.og_description), $fmt.CHECKMARK, { title: 'Strings', width: -80 });
        def.addValue(p => !!p.page_metadata.in_sitemap, $fmt.CHECKMARK, { title: 'Sitemap', width: -75 });
        def.addValue(p => !!p.page_metadata.no_index, $fmt.CHECKMARK, { title: 'noindex', width: -80 });
        def.addValue(p => !!p.page_metadata.no_follow, $fmt.CHECKMARK, { title: 'nofollow', width: -85 });

        def.addButtons([
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Delete', action: item => {
                    return showWarning({ title: item.path, text: "Are you sure you want to delete this route?" }).then(() => {
                        return f_routes.remove(item.id).then(() => {
                            doSearch();
                        });
                    });
                }, when: item => xavis_experiments || !item.is_locked
            },
            {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => {
                    $location.push('route', { route_id: item.id });
                }
            }
        ]);

        def.controller = row => {
            if(row.item.is_hidden)
                row.classList.add('tl-standby-row');
        };

        def.preference_prefix = 'routes';

        const source = new YArrayDataSource<FRoute>;

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block m-widest', [
                filter_bar,
                new UITable(def, source),
            ])),
            makeActionButton(UIIcon.PLUS, 'New route', () => { $location.push('route') })
        ]);

        doSearch();
        this.onViewDidReappear = doSearch;
    }
}
